import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  InputNumber,
  Input,
  Select,
  Button,
  Space,
  Divider,
  Tag,
  Typography,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CalculatorOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";

const { Text } = Typography;
const { Option } = Select;

const JobSpecsPanel = ({ job, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [optimizedOption, setOptimizedOption] = useState(null);
  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [form] = Form.useForm();

  // ── Calculation helpers ──────────────────────────────────
  const calculateNetGsm = (layers) => {
    if (!layers) return 0;
    const layersArr = typeof layers === "string" ? JSON.parse(layers) : layers;
    if (!Array.isArray(layersArr) || layersArr.length === 0) return 0;
    let netGsm = 0;
    layersArr.forEach((layer) => {
      if (layer.layerType === "flute") {
        netGsm += parseInt(layer.gsm || 0) * parseFloat(layer.ff || 1);
      } else {
        netGsm += parseInt(layer.gsm || 0);
      }
    });
    return Math.round(netGsm * 100) / 100;
  };

  const calculateBoxWeight = (rs, cs, netGsm, sheetsPerBox) => {
    if (!rs || !cs || !netGsm) return 0;
    const n = sheetsPerBox || 1;
    return Math.round(((rs * cs * netGsm * n) / 1550000) * 1000) / 1000;
  };

  const getInitialValues = (j) => {
    // Compute Net GSM from layers if stored value is missing/zero
    const computedNetGsm = j.net_gsm || calculateNetGsm(j.layers);
    // Single-box dimensions (calc RS/CS)
    const singleRS = j.rs || 0;
    const singleCS = j.cs || 0;
    const m = j.boxes_per_sheet || 1;
    const n = j.sheets_per_box || 1;
    // Box Weight = weight of ONE box, always uses single-box RS × CS
    const computedBoxWeight = j.box_weight || calculateBoxWeight(singleRS || j.roll_size || 0, singleCS || j.cutting_size || 0, computedNetGsm, n);

    return {
      // Box Dimensions (from order — read-only)
      length: j.length,
      width: j.width,
      height: j.height,
      unit_type: j.unit_type,
      ply: j.ply,
      casting_type: j.casting_type,
      subcategory: j.subcategory,
      // Calculated reference fields — single box dimensions (always read-only)
      rs: singleRS || j.roll_size || 0,
      cs: singleCS || j.cutting_size || 0,
      calc_sheets_per_box: n,
      // Editable production fields (may be optimized multi-up)
      roll_size: j.roll_size || singleRS || 0,
      cutting_size: j.cutting_size || singleCS || 0,
      boxes_per_sheet: j.boxes_per_sheet || m || 1,
      sheets_per_box: j.sheets_per_box || n || 1,
      box_type: j.box_type,
      net_gsm: computedNetGsm,
      box_weight: computedBoxWeight,
      ordered_quantity: j.ordered_quantity,
      production_quantity: j.production_quantity,
      joining_method: j.joining_method,
    };
  };

  useEffect(() => {
    if (job) {
      const vals = getInitialValues(job);
      form.setFieldsValue(vals);
      // Auto-calculate if RS/CS are missing
      if (!job.rs || !job.cs || job.rs === 0 || job.cs === 0) {
        runCalculation(vals);
      }
    }
  }, [job]);

  // Recalculate box weight using single-box RS/CS (calc), NOT optimized roll_size
  const recalcBoxWeight = () => {
    const rs = form.getFieldValue("rs") || 0;
    const cs = form.getFieldValue("cs") || 0;
    const netGsm = form.getFieldValue("net_gsm") || 0;
    const n = form.getFieldValue("sheets_per_box") || 1;
    const weight = calculateBoxWeight(rs, cs, netGsm, n);
    form.setFieldsValue({ box_weight: weight });
  };

  const runCalculation = async (overrideVals) => {
    const vals = overrideVals || form.getFieldsValue([
      "length", "width", "height", "unit_type", "subcategory", "casting_type", "production_quantity"
    ]);
    if (!vals.length || !vals.width) return; // height can be 0 for plates
    try {
      setCalculating(true);
      const { data } = await axios.post("job/calculate-specs", {
        length: vals.length,
        width: vals.width,
        height: vals.height,
        unit_type: vals.unit_type,
        subcategory: vals.subcategory,
        casting_type: vals.casting_type,
        quantity: vals.production_quantity,
        selected_machines: job?.selected_machines || [],
      });
      const calcN = data.N > 1 ? data.N : 1;
      // Update calc reference fields (read-only)
      form.setFieldsValue({
        rs: data.RS,
        cs: data.CS,
        calc_sheets_per_box: calcN,
      });
      // Copy calc values into editable fields (M=1, standard single-box sheet)
      form.setFieldsValue({
        roll_size: data.RS,
        cutting_size: data.CS,
        boxes_per_sheet: 1,
        sheets_per_box: calcN,
      });
      // Capture roll size optimization option
      setOptimizedOption(data.optimized || null);
      // Recalculate box weight after RS/CS update
      recalcBoxWeight();
    } catch (e) {
      // silent — user can still edit manually
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      await axios.put(`job/${job.id}`, {
        length: values.length,
        width: values.width,
        height: values.height,
        unit_type: values.unit_type,
        ply: values.ply,
        casting_type: values.casting_type,
        subcategory: values.subcategory,
        roll_size: values.roll_size,
        cutting_size: values.cutting_size,
        rs: values.rs,
        cs: values.cs,
        boxes_per_sheet: values.boxes_per_sheet,
        sheets_per_box: values.sheets_per_box,
        box_type: values.box_type,
        net_gsm: values.net_gsm,
        box_weight: values.box_weight,
        production_quantity: values.production_quantity,
        joining_method: values.joining_method,
      });

      toast.success("Job specifications updated successfully");
      setEditing(false);
      setOptimizedOption(null);
      setInventoryOptions([]);

      // Recalculate stages & machines based on new specs
      try {
        await axios.post(`job/${job.id}/recalculate-stages`);
        await axios.post(`job/${job.id}/auto-select-machines`);
      } catch (_) { /* non-critical */ }

      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update job specifications");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue(getInitialValues(job));
    setOptimizedOption(null);
    setInventoryOptions([]);
    setEditing(false);
  };

  const handleRecalculate = async () => {
    try {
      setCalculating(true);
      const vals = form.getFieldsValue();
      const { data } = await axios.post("job/calculate-specs", {
        length: vals.length,
        width: vals.width,
        height: vals.height || 0,
        unit_type: vals.unit_type,
        subcategory: vals.subcategory,
        casting_type: vals.casting_type,
        quantity: vals.production_quantity,
        selected_machines: job?.selected_machines || [],
        layers: job?.layers || "[]",
        ply: vals.ply,
      });
      const calcN = data.N > 1 ? data.N : 1;
      const newRS = data.RS || 0;
      const newCS = data.CS || 0;
      const netGsm = calculateNetGsm(job.layers);
      // Box weight uses single-box calc RS/CS
      const boxWeight = calculateBoxWeight(newRS, newCS, netGsm, calcN);

      // Update form locally — don't save, don't reload
      form.setFieldsValue({
        rs: newRS,
        cs: newCS,
        roll_size: newRS,
        cutting_size: newCS,
        boxes_per_sheet: 1,
        sheets_per_box: calcN,
        calc_sheets_per_box: calcN,
        net_gsm: netGsm,
        box_weight: boxWeight,
      });

      // Show optimization option if available
      setOptimizedOption(data.optimized || null);
      setInventoryOptions(data.inventory_options || []);
      if (!editing) setEditing(true);
      toast.success("Specs recalculated — click Save to apply");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to recalculate");
    } finally {
      setCalculating(false);
    }
  };

  if (!job) return null;

  return (
    <Card
      title={
        <Space>
          <CalculatorOutlined />
          <span>Job Specifications</span>
          <Tag color="blue">Auto-Calculated</Tag>
        </Space>
      }
      extra={
        editing ? (
          <Space>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          </Space>
        ) : (
          <Button
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
          >
            Edit Specifications
          </Button>
        )
      }
      style={{ marginBottom: 16 }}
    >
      <Form form={form} layout="vertical" disabled={!editing}>
        {/* Box Dimensions — always disabled, inherited from order */}
        <Divider orientation="left" plain>
          <Space>
            <Text strong>Box Dimensions</Text>
            <Tag color="orange" style={{ fontSize: 11 }}>From Order</Tag>
          </Space>
        </Divider>
        <Row gutter={16}>
          <Col xs={12} sm={6} md={4}>
            <Form.Item
              name="length"
              label={
                <Tooltip title="Length of the box (from order)">
                  <Space>L <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                placeholder="Length"
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item
              name="width"
              label={
                <Tooltip title="Width of the box (from order)">
                  <Space>B <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                placeholder="Width"
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item
              name="height"
              label={
                <Tooltip title="Height of the box (from order)">
                  <Space>H <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                placeholder="Height"
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item name="unit_type" label="Unit">
              <Select placeholder="Unit" disabled>
                <Option value="mm">mm</Option>
                <Option value="inches">inches</Option>
                <Option value="cm">cm</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item name="ply" label="Ply">
              <Select placeholder="Ply" disabled>
                <Option value={3}>3 Ply</Option>
                <Option value={5}>5 Ply</Option>
                <Option value={7}>7 Ply</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item name="casting_type" label="Casting">
              <Select placeholder="Casting Type" disabled>
                <Option value="DIE">DIE</Option>
                <Option value="ROTARY">ROTARY</Option>
                <Option value="ROTARY-SLOT">ROTARY-SLOT</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Sheet Dimensions */}
        <Divider orientation="left" plain>
          <Space>
            <Text strong>Sheet Dimensions</Text>
            <Tooltip title="Recalculate RS, CS, Net GSM, Box Weight from box dimensions">
              <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={handleRecalculate} loading={calculating}>
                Recalculate
              </Button>
            </Tooltip>
          </Space>
        </Divider>
        <Row gutter={16}>
          {/* Editable: Roll Size */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="roll_size"
              label={
                <Tooltip title="Production RS - Actual roll width for production (may include multi-up optimization). Editable override.">
                  <Space>Production RS <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                addonAfter='"'
                onChange={editing ? () => setTimeout(recalcBoxWeight, 100) : undefined}
              />
            </Form.Item>
          </Col>
          {/* Editable: Cutting Size */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="cutting_size"
              label={
                <Tooltip title="Production CS - Actual cut length for production. Editable override.">
                  <Space>Production CS <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                addonAfter='"'
                onChange={editing ? () => setTimeout(recalcBoxWeight, 100) : undefined}
              />
            </Form.Item>
          </Col>
          {/* Read-only: RS (Calc) */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="rs"
              label={
                <Tooltip title="Calculated RS from rate calculator (read-only reference)">
                  <Space>RS (Calc) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%", background: "#f5f5f5" }}
                min={0}
                precision={2}
                addonAfter='"'
                disabled
              />
            </Form.Item>
          </Col>
          {/* Read-only: CS (Calc) */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="cs"
              label={
                <Tooltip title="Calculated CS from rate calculator (read-only reference)">
                  <Space>CS (Calc) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%", background: "#f5f5f5" }}
                min={0}
                precision={2}
                addonAfter='"'
                disabled
              />
            </Form.Item>
          </Col>
          {/* Editable: Boxes/Sheet (M) */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="boxes_per_sheet"
              label={
                <Tooltip title="M - Number of boxes made from one sheet. Editable override.">
                  <Space>Boxes/Sheet (M) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber style={{ width: "100%" }} min={1} precision={0} />
            </Form.Item>
          </Col>
          {/* Editable: Sheets/Box (N) */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="sheets_per_box"
              label={
                <Tooltip title="N - Number of sheets required for one box. Editable override.">
                  <Space>Sheets/Box (N) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                precision={0}
                onChange={editing ? () => setTimeout(recalcBoxWeight, 100) : undefined}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          {/* Read-only: N (Calc) */}
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="calc_sheets_per_box"
              label={
                <Tooltip title="Calculated N from rate calculator (read-only reference)">
                  <Space>N (Calc) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%", background: "#f5f5f5" }}
                min={1}
                precision={0}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Roll Size Optimization Suggestion */}
        {optimizedOption && (
          <div
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: 6,
              padding: "10px 16px",
              marginBottom: 16,
            }}
          >
            <Row align="middle" gutter={16}>
              <Col flex="auto">
                <Text strong style={{ color: "#389e0d" }}>
                  ⚡ Roll Size Optimization Available
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {optimizedOption.note}
                </Text>
                <br />
                <Space style={{ marginTop: 4 }}>
                  <Tag color="green">RS: {optimizedOption.roll_size}"</Tag>
                  <Tag color="blue">CS: {optimizedOption.cutting_size}"</Tag>
                  <Tag color="orange">M = {optimizedOption.boxes_per_sheet}</Tag>
                  {optimizedOption.sheets_needed && (
                    <Tag color="purple">Sheets: {optimizedOption.sheets_needed}</Tag>
                  )}
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="small"
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                  onClick={() => {
                    const opt = optimizedOption;
                    const netGsm = form.getFieldValue("net_gsm") || 0;
                    const n = opt.sheets_per_box || 1;
                    // Box weight uses single-box RS/CS (calc), not optimized roll_size
                    const singleRS = form.getFieldValue("rs") || 0;
                    const singleCS = form.getFieldValue("cs") || 0;
                    const bw = calculateBoxWeight(singleRS, singleCS, netGsm, n);
                    form.setFieldsValue({
                      roll_size: opt.roll_size,
                      cutting_size: opt.cutting_size,
                      boxes_per_sheet: opt.boxes_per_sheet,
                      sheets_per_box: n,
                      box_weight: bw,
                    });
                    setOptimizedOption(null);
                    if (!editing) setEditing(true);
                    toast.success(`Applied optimized roll size: M=${opt.boxes_per_sheet} — click Save to apply`);
                  }}
                >
                  ✓ Apply Optimization
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {/* Inventory-based RS Options */}
        {inventoryOptions.length > 0 && inventoryOptions.map((opt, idx) => (
          <div
            key={idx}
            style={{
              background: opt.type === "inventory_above" ? "#e6f7ff" : "#fff7e6",
              border: `1px solid ${opt.type === "inventory_above" ? "#91d5ff" : "#ffd591"}`,
              borderRadius: 6,
              padding: "10px 16px",
              marginBottom: 8,
            }}
          >
            <Row align="middle" gutter={16}>
              <Col flex="auto">
                <Text strong style={{ color: opt.type === "inventory_above" ? "#096dd9" : "#d46b08" }}>
                  {opt.type === "inventory_above" ? "📦 Stock Available (Above)" : "📦 Stock Available (Below)"}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {opt.note}
                </Text>
                <br />
                <Space style={{ marginTop: 4 }}>
                  <Tag color="cyan">Reel: {opt.reel_width}"</Tag>
                  <Tag color="green">RS: {opt.roll_size}"</Tag>
                  <Tag color="blue">CS: {opt.cutting_size}"</Tag>
                  <Tag color="orange">M = {opt.boxes_per_sheet}</Tag>
                  {opt.sheets_needed && <Tag color="purple">Sheets: {opt.sheets_needed}</Tag>}
                  {opt.waste_per_sheet > 0 && <Tag color="red">Waste: {opt.waste_per_sheet}"</Tag>}
                </Space>
              </Col>
              <Col>
                <Button
                  size="small"
                  type="primary"
                  style={{
                    background: opt.type === "inventory_above" ? "#1890ff" : "#fa8c16",
                    borderColor: opt.type === "inventory_above" ? "#1890ff" : "#fa8c16",
                  }}
                  onClick={() => {
                    const netGsm = form.getFieldValue("net_gsm") || 0;
                    const n = opt.sheets_per_box || 1;
                    const singleRS = form.getFieldValue("rs") || 0;
                    const singleCS = form.getFieldValue("cs") || 0;
                    const bw = calculateBoxWeight(singleRS, singleCS, netGsm, n);
                    form.setFieldsValue({
                      roll_size: opt.roll_size,
                      cutting_size: opt.cutting_size,
                      boxes_per_sheet: opt.boxes_per_sheet,
                      sheets_per_box: n,
                      box_weight: bw,
                    });
                    setInventoryOptions([]);
                    setOptimizedOption(null);
                    if (!editing) setEditing(true);
                    toast.success(`Applied ${opt.reel_width}" reel: M=${opt.boxes_per_sheet} — click Save to apply`);
                  }}
                >
                  ✓ Use This Reel
                </Button>
              </Col>
            </Row>
          </div>
        ))}

        {/* Production Info */}
        <Divider orientation="left" plain>
          <Text strong>Production Information</Text>
        </Divider>
        <Row gutter={16}>
          <Col xs={12} sm={6} md={4}>
            <Form.Item name="subcategory" label="Box Type">
              <Input placeholder="Box Type" />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item name="box_type" label="Inner/Outer">
              <Select placeholder="Inner/Outer" allowClear>
                <Option value="inner">Inner</Option>
                <Option value="outer">Outer</Option>
              </Select>
            </Form.Item>
          </Col>
          <Form.Item name="net_gsm" hidden>
            <InputNumber />
          </Form.Item>
          <Col xs={12} sm={6} md={4}>
            <Form.Item
              name="box_weight"
              label={
                <Tooltip title="Weight of ONE box/plate in kg = (single-box RS × CS × Net GSM × N) / 1,550,000. Uses calc RS/CS, not optimized roll size.">
                  <Space>Box Weight <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={3}
                addonAfter="kg"
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item name="joining_method" label="Finishing">
              <Select placeholder="Joining Method">
                <Option value="stitching">Stitching</Option>
                <Option value="hand_pasting">Hand Pasting</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Quantities */}
        <Divider orientation="left" plain>
          <Text strong>Quantities</Text>
        </Divider>
        <Row gutter={16}>
          <Col xs={12} sm={6} md={4}>
            <Form.Item
              name="ordered_quantity"
              label={
                <Tooltip title="Number of boxes/pieces ordered by customer">
                  <Space>Ordered Qty (Boxes) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={0}
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item
              name="production_quantity"
              label={
                <Tooltip title="Number of boxes/pieces to produce (ordered qty + wastage buffer)">
                  <Space>Production Qty (Boxes) <InfoCircleOutlined /></Space>
                </Tooltip>
              }
            >
              <InputNumber style={{ width: "100%" }} min={0} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item label={
              <Tooltip title="Sheets to produce = ⌈Production Qty × Sheets/Box (N) ÷ Boxes/Sheet (M)⌉">
                <Space>Sheets to Produce <InfoCircleOutlined /></Space>
              </Tooltip>
            }>
              {(() => {
                const prodQty = form.getFieldValue("production_quantity") || 0;
                const n = form.getFieldValue("sheets_per_box") || 1;
                const m = form.getFieldValue("boxes_per_sheet") || 1;
                const sheets = Math.ceil((prodQty * n) / m);
                return (
                  <InputNumber
                    style={{ width: "100%", background: "#f5f5f5", fontWeight: "bold" }}
                    value={sheets}
                    disabled
                    precision={0}
                  />
                );
              })()}
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Item label={
              <Tooltip title="Total weight of all sheets = Sheets × RS × CS × Net GSM ÷ 1,550,000">
                <Space>Total Sheet Weight <InfoCircleOutlined /></Space>
              </Tooltip>
            }>
              {(() => {
                const prodQty = form.getFieldValue("production_quantity") || 0;
                const n = form.getFieldValue("sheets_per_box") || 1;
                const m = form.getFieldValue("boxes_per_sheet") || 1;
                const sheets = Math.ceil((prodQty * n) / m);
                const rs = form.getFieldValue("roll_size") || form.getFieldValue("rs") || 0;
                const cs = form.getFieldValue("cutting_size") || form.getFieldValue("cs") || 0;
                const netGsm = form.getFieldValue("net_gsm") || 0;
                const totalKg = Math.round(((sheets * rs * cs * netGsm) / 1550000) * 1000) / 1000;
                return (
                  <InputNumber
                    style={{ width: "100%", background: "#f5f5f5", fontWeight: "bold" }}
                    value={totalKg}
                    disabled
                    precision={3}
                    addonAfter="kg"
                  />
                );
              })()}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default JobSpecsPanel;
